/**
 * Abortable sleep helpers.
 *
 * Both helpers wrap a `setTimeout` with an `AbortSignal` listener so callers
 * can cancel the wait promptly without leaking an outstanding timer or
 * orphaning the listener. They differ only in how they communicate abort:
 *
 * - {@link abortableSleep} resolves with `false` on abort, `true` on timeout.
 *   Use when the caller owns flow control via the returned boolean (most
 *   poll loops, retry helpers).
 * - {@link sleepOrThrow} rejects with the signal's `reason` on abort. Use
 *   when the caller wants to short-circuit an `await` chain via try/catch.
 *
 * Listener-cleanup invariant: both helpers remove the `'abort'` listener on
 * BOTH the timeout-fires and the abort-fires path so the signal never
 * accumulates listeners across many sleeps. The `{ once: true }` option on
 * the listener is belt-and-suspenders for the abort path.
 *
 * Replaces five copies of the same helper that drifted across the
 * codebase: `defaultSleep` in `lib/ts-commons-db-resilience/src/warmup.ts` and
 * `famfam/lib/server/threads/family-thread-feed.ts`, the inline `sleep`
 * closure in `famfam/components/hooks/use-family-thread-feed.ts`,
 * `abortableDelay` in `famfam/lib/dev/mock-mode-services.ts`, and the
 * former private `sleep` in `lib/llm-clients/src/llm/with-retry.ts`
 * (migrated to `sleepOrThrow` in FPO-3235).
 */

/**
 * Sleep for `ms` milliseconds, with optional `AbortSignal` cancellation.
 *
 * Resolves to `true` when the timeout fires normally, `false` when the
 * signal is aborted (either before the call or during the wait). When the
 * signal is `undefined`, behaves like a plain `setTimeout` and always
 * resolves `true`.
 */
export function abortableSleep(ms: number, signal: AbortSignal | undefined): Promise<boolean> {
  if (signal?.aborted) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    const onAbort = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      resolve(false);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve(true);
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Sleep for `ms` milliseconds, with optional `AbortSignal` cancellation.
 *
 * Resolves with `undefined` on timeout. On abort, REJECTS with the
 * signal's `reason` (or a fresh `DOMException('Aborted', 'AbortError')`
 * if the signal has no reason). Use this variant when the caller wants
 * try/catch flow rather than checking a boolean return.
 */
export function sleepOrThrow(ms: number, signal: AbortSignal | undefined): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(abortReason(signal));
  }
  return new Promise<void>((resolve, reject) => {
    const onAbort = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      reject(abortReason(signal));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Returns the signal's reason if present, or a generic AbortError DOMException as the rejection value. */
function abortReason(signal: AbortSignal | undefined): unknown {
  return signal?.reason ?? new DOMException('Aborted', 'AbortError');
}
